<script lang="ts" setup>
  import { useCurrency, useProducts } from '@/composition'
  import { useProductListStore } from '@/store/productListStore'
  import { Image01Icon, PlusIcon } from '@gohighlevel/ghl-icons/24/outline'
  import {
    AddNewProductItem,
    BusinessProduct,
    BusinessProductOption,
    BusinessProductPrice,
    BusinessProductPriceOption,
    PriceTypeEnum,
    ProductLineEvents,
  } from '@gohighlevel/ghl-proposals-common'
  import {
    UIButton,
    UIForm,
    UIFormItem,
    UIInputNumber,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UISelect,
    UISwitch,
    UITextSmMedium,
  } from '@gohighlevel/ghl-ui'
  import { debounce } from 'lodash'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'

  const PRODUCTS_PAGE_SIZE = 100

  const productListStore = useProductListStore()
  const { getCurrencySymbol } = useCurrency()
  const { loading: productsLoading, fetchBusinessProducts } = useProducts()

  const props = defineProps<{
    productList: BusinessProductOption[]
    show: boolean
    loading: boolean
    elementVersion: number
    loadMoreLoading?: boolean
    hasMoreProducts?: boolean
  }>()

  const SCROLL_NEAR_BOTTOM_THRESHOLD = 20

  const emit = defineEmits([
    ProductLineEvents.LOAD_MORE_PRODUCTS,
    ProductLineEvents.OPEN_ADD_PRODUCT_MODAL,
    ProductLineEvents.CLOSE_ADD_ITEM_MODAL,
    ProductLineEvents.ADD_LINE_ITEM_TO_LIST,
  ])

  const productsPriceList = ref<BusinessProductPriceOption[]>([])
  const searchResults = ref<BusinessProduct[]>([])
  const searchEnabled = ref(false)
  const searchOffset = ref(0)
  const searchHasMore = ref(true)
  const searchLoadMoreLoading = ref(false)
  const resetMenuOnOptionsChange = ref(true)
  const products = computed(() => {
    // If search is enabled, return search results else return the default product list
    const list = searchEnabled.value ? searchResults.value : props.productList
    return mutateProductListToOptions(list)
  })

  const formValue = ref<AddNewProductItem>({
    productId: '',
    priceId: '',
    setupFee: 0,
    priceType: '',
    showProductImageInList: false,
    imageUrl: '',
  })
  const searchText = ref('')
  const currencySymbol = ref()
  const currencyCode = ref()

  const productOptions = computed(() => {
    return products.value.map((product: any) => {
      return {
        label: product.name,
        value: product._id,
        disabled: product.disabled,
      }
    })
  })

  const selectLoading = computed(() => productsLoading.value || props.loadMoreLoading || searchLoadMoreLoading.value)

  const mutateProductListToOptions = (list: BusinessProduct[]) => {
    return list
      .map((product: BusinessProduct) => {
        if (!product.prices) return
        return {
          ...product,
          label: product.name,
          value: product._id,
          disabled: isProductDisabled(product),
          prices: product?.prices.map((price: BusinessProductPrice) => {
            return {
              label: price?.name,
              value: price?._id,
              disabled: isPriceDisabled(price),
              ...price,
            }
          }),
        }
      })
      .filter(Boolean) as BusinessProduct[]
  }

  const resetFormValue = () => {
    formValue.value = {
      productId: '',
      priceId: '',
      setupFee: 0,
      priceType: '',
      showProductImageInList: false,
      imageUrl: '',
    }
    productsPriceList.value = []
  }

  const clearQuery = () => {
    searchText.value = ''
  }

  const search = debounce(async (text: string) => {
    searchText.value = text
    searchOffset.value = 0
    searchHasMore.value = true
    resetMenuOnOptionsChange.value = true
    if (text) {
      searchEnabled.value = true
      const response = await fetchBusinessProducts(text, PRODUCTS_PAGE_SIZE, 0)
      // Only apply initial search results if no load-more ran in the meantime (avoids race where load-more response arrives first and would be overwritten)
      if (searchOffset.value === 0) {
        searchResults.value = mutateProductListToOptions(response?.products ?? [])
        const total = response?.total?.[0]?.total ?? 0
        searchHasMore.value = searchResults.value.length < total
      }
    } else {
      searchEnabled.value = false
      searchResults.value = []
    }
  }, 300)

  const loadMoreSearchResults = async () => {
    if (!searchText.value || searchLoadMoreLoading.value || !searchHasMore.value) return
    resetMenuOnOptionsChange.value = false
    searchLoadMoreLoading.value = true
    try {
      const offset = searchOffset.value + PRODUCTS_PAGE_SIZE
      const response = await fetchBusinessProducts(searchText.value, PRODUCTS_PAGE_SIZE, offset)
      const newProducts = mutateProductListToOptions(response?.products ?? [])
      searchResults.value = [...searchResults.value, ...newProducts]
      searchOffset.value = offset
      const total = response?.total?.[0]?.total ?? 0
      searchHasMore.value = searchResults.value.length < total
    } finally {
      searchLoadMoreLoading.value = false
    }
  }

  const debouncedLoadMore = debounce((el: HTMLElement) => {
    if (!props.show) return
    if (!el || el.scrollHeight <= el.clientHeight) return
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_NEAR_BOTTOM_THRESHOLD
    if (!atEnd) return
    if (searchEnabled.value) {
      if (searchLoadMoreLoading.value || !searchHasMore.value) return
      loadMoreSearchResults()
    } else {
      if (!props.hasMoreProducts || props.loadMoreLoading) return
      resetMenuOnOptionsChange.value = false
      emit(ProductLineEvents.LOAD_MORE_PRODUCTS)
    }
  }, 200)

  function onScroll(event: Event) {
    const el = (event?.target ?? event?.currentTarget) as HTMLElement | undefined
    if (el) debouncedLoadMore(el)
  }

  watch(
    () => props.show,
    show => {
      if (!show) {
        debouncedLoadMore.cancel()
      }
    }
  )

  onBeforeUnmount(() => {
    debouncedLoadMore.cancel()
  })

  const handleSelectProduct = (value: string) => {
    formValue.value.productId = value
    formValue.value.priceId = ''
    const currentList = searchEnabled.value ? searchResults.value : props.productList
    productsPriceList.value = (currentList?.find(item => item._id === formValue.value.productId)?.prices ||
      []) as BusinessProductPriceOption[]
    if (productsPriceList.value.length >= 1) {
      formValue.value.priceId = productsPriceList.value[0]._id
      handleSelectProductPrice(formValue.value.priceId)
    }
  }

  const handleSelectProductPrice = (priceId: string) => {
    const price = productsPriceList.value.find(priceItem => priceItem._id === priceId)
    if (price) {
      currencySymbol.value = getCurrencySymbol(price.currency)
      currencyCode.value = price.currency
      formValue.value.priceType = price.type
      formValue.value.setupFee = (price as BusinessProductPrice & { setupFee?: number })?.setupFee || 0
    }
  }

  function isProductDisabled(product: BusinessProduct) {
    if (productListStore.currency) {
      const showProduct = product?.prices?.some((item: BusinessProductPrice) => item.currency === productListStore.currency)
      return !showProduct
    }

    return false
  }

  function isPriceDisabled(price: BusinessProductPrice) {
    if (!productListStore.currency) {
      return false
    }
    return price.currency != productListStore.currency
  }

  const productImageSrc = computed(() => {
    return products.value.find(product => product._id === formValue.value.productId)?.image
  })
  const onToggleSwitch = (value: boolean) => {
    formValue.value.showProductImageInList = value
    formValue.value.imageUrl = productImageSrc.value || ''
  }
</script>

<template>
  <UIModal :show="props.show">
    <template #header>
      <div class="py-2">
        <UIButton
          id="add-new-item-btn"
          class="border-0 w-full px-2 py-7 text-primary-700 rounded-lg bg-primary-50"
          type="tertiary"
          @click="() => $emit(ProductLineEvents.OPEN_ADD_PRODUCT_MODAL)"
        >
          <PlusIcon class="w-5 h-5 mr-2" />
          {{ $t('common.newItem') }}
        </UIButton>
      </div>
    </template>
    <UIModalContent>
      <UIForm id="add-product-form">
        <UIFormItem :label="$t('common.selectProduct')" path="productId" required>
          <UISelect
            class="product-select"
            filterable
            id="select-product-id"
            remote
            :size="'large'"
            :placeholder="$t('common.selectProduct')"
            :value="formValue.productId"
            :options="productOptions"
            :loading="selectLoading"
            :reset-menu-on-options-change="resetMenuOnOptionsChange"
            @update:value="handleSelectProduct"
            @search="search"
            @scroll="onScroll"
            @update:show="(show: boolean) => !show && clearQuery()"
            @focus="search('')"
            @click="search('')"
          />
        </UIFormItem>
        <UIFormItem :label="$t('common.price')" path="priceId" required>
          <UISelect
            id="products-price-item"
            v-model:value="formValue.priceId"
            :loading="props.loading"
            :options="productsPriceList"
            :placeholder="$t('common.selectPrice')"
            @change="handleSelectProductPrice"
          />
        </UIFormItem>

        <UIFormItem v-if="formValue.priceType === PriceTypeEnum.RECURRING" :label="$t('common.setupFee')" path="setupFee">
          <div class="w-full">
            <UIInputNumber
              id="product-price-setupFee"
              v-model="formValue.setupFee"
              placeholder="0"
              :show-button="false"
              :precision="2"
              :min="0"
              size="large"
              class="w-full"
            >
              <template #prefix>{{ currencySymbol }}</template>
              <template #suffix>{{ currencyCode }}</template>
            </UIInputNumber>
          </div>
        </UIFormItem>

        <div class="flex items-center mb-4" v-if="props.elementVersion >= 3">
          <UISwitch
            @update:value="(val: boolean) => onToggleSwitch(val)"
            id="activeRow.optional"
            :value="formValue.showProductImageInList || false"
          />
          <UITextSmMedium class="ml-2">{{ $t('common.showProductImageInList') }}</UITextSmMedium>
        </div>
        <div v-if="formValue.showProductImageInList" class="w-[120px] h-[120px] object-cover overflow-hidden rounded-sm border border-1">
          <img
            v-if="productImageSrc"
            :src="productImageSrc"
            :alt="$t('common.productImageAlt')"
            class="max-w-full m-auto object-cover w-[120px] h-[120px]"
          />
          <Image01Icon v-else class="w-[120px] h-[120px] text-gray-300" />
        </div>
      </UIForm>
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="add-new-product-modal-footer"
        :negative-text="$t('common.cancel')"
        :positive-text="$t('common.addItem')"
        @negativeClick="
          () => {
            $emit(ProductLineEvents.CLOSE_ADD_ITEM_MODAL)
            resetFormValue()
          }
        "
        @positiveClick="
          () => {
            $emit(ProductLineEvents.ADD_LINE_ITEM_TO_LIST, formValue)
            resetFormValue()
          }
        "
      />
    </template>
  </UIModal>
</template>

<style>
  .n-modal-container {
    z-index: 2020 !important;
  }
</style>
